import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";

export default function BanAppeal() {
  const { session } = useAuth();
  const [loggingIn, setLoggingIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    roblox_username: "",
    ban_reason: "",
    appeal_text: "",
    evidence: "",
  });

  function set(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleDiscordLogin() {
    setLoggingIn(true);
    await supabase.auth.signInWithOAuth({
      provider: "discord",
      options: { redirectTo: window.location.href },
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    try {
      const discordIdentity = session?.user?.identities?.find(i => i.provider === "discord");
      const discordId = discordIdentity?.id || session?.user?.user_metadata?.provider_id;
      const discordUsername = session?.user?.user_metadata?.full_name || session?.user?.user_metadata?.name;

      if (!discordId) throw new Error("Could not retrieve your Discord ID. Please sign out and back in.");

      // Check for existing pending appeal
      const { data: existing } = await supabase
        .from("ban_appeals")
        .select("id, status")
        .eq("discord_id", discordId)
        .in("status", ["pending"])
        .maybeSingle();

      if (existing) {
        throw new Error("You already have a pending appeal. Please wait for a decision before submitting another.");
      }

      // Insert appeal
      const { data: appeal, error: insertError } = await supabase
        .from("ban_appeals")
        .insert({
          discord_id: discordId,
          discord_username: discordUsername,
          roblox_username: form.roblox_username.trim(),
          ban_reason: form.ban_reason.trim(),
          appeal_text: form.appeal_text.trim(),
          evidence: form.evidence.trim() || null,
          status: "pending",
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // Send embed to Discord appeals channel
      const embedRes = await fetch("/api/send-appeal-embed", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "create",
          secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
          appeal_id: appeal.id,
          discord_id: discordId,
          discord_username: discordUsername,
          roblox_username: form.roblox_username.trim(),
          ban_reason: form.ban_reason.trim(),
          appeal_text: form.appeal_text.trim(),
          evidence: form.evidence.trim() || null,
        }),
      });

      const embedResult = await embedRes.json();

      // Store message_id so we can update the embed later
      if (embedResult.message_id) {
        await supabase
          .from("ban_appeals")
          .update({ discord_message_id: embedResult.message_id })
          .eq("id", appeal.id);
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    }

    setSubmitting(false);
  }

  const discordUser = session?.user?.user_metadata;
  const inputClass =
    "w-full rounded-xl border border-[#ffb56f]/30 bg-black/50 px-4 py-3 font-bold text-white outline-none placeholder:text-white/40 focus:border-[#ffd978] resize-none";

  return (
    <main className="min-h-screen bg-[#050505] text-white">
      <Header />

      <section className="px-6 pt-36 pb-20 md:px-[7%]">
        <p className="font-black uppercase text-[#ffb56f]">Community</p>
        <h1 className="outfall-heading text-[clamp(58px,9vw,120px)] leading-none text-[#ffd978] drop-shadow-[0_0_18px_#ff8f6b]">
          Ban Appeal
        </h1>
        <p className="mt-4 max-w-2xl font-bold text-white/50">
          If you believe your ban was a mistake, submit an appeal below. Be honest — appeals with false information will be permanently denied.
        </p>

        <div className="mt-10 max-w-3xl">
          {submitted ? (
            <div className="rounded-[24px] border-4 border-[#ffd978] bg-black/60 p-8 shadow-[0_0_35px_rgba(255,143,107,0.35)]">
              <p className="text-4xl font-black text-[#ffd978]">✅ Appeal Submitted</p>
              <p className="mt-3 font-bold text-white/60">
                Your appeal has been received and sent to our staff team. You'll receive a Discord DM when a decision is made.
              </p>
              <Link to="/home" className="outfall-button mt-6 inline-block">Back to Home</Link>
            </div>
          ) : !session ? (
            <div className="rounded-[24px] border-4 border-[#ffd978] bg-black/60 p-8 shadow-[0_0_35px_rgba(255,143,107,0.35)]">
              <p className="mb-6 font-bold text-white/60">
                Sign in with the same Discord account that is banned so we can identify you.
              </p>
              <button
                onClick={handleDiscordLogin}
                disabled={loggingIn}
                className="flex items-center gap-3 rounded-xl bg-[#5865F2] px-6 py-4 font-black text-white transition hover:bg-[#4752C4] disabled:opacity-50"
              >
                <svg width="24" height="18" viewBox="0 0 24 18" fill="currentColor">
                  <path d="M20.317 1.492a19.825 19.825 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.294 18.294 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 1.492a.07.07 0 0 0-.032.027C.533 6.093-.32 10.555.099 14.961a.08.08 0 0 0 .031.055 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.442a.061.061 0 0 0-.031-.028z"/>
                </svg>
                {loggingIn ? "Redirecting…" : "Continue with Discord"}
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="rounded-[24px] border-4 border-[#ffd978] bg-black/60 p-8 shadow-[0_0_35px_rgba(255,143,107,0.35)]"
            >
              {/* Logged in as */}
              <div className="mb-8 flex items-center gap-3 rounded-xl border border-[#5865F2]/30 bg-[#5865F2]/10 px-4 py-3">
                {discordUser?.avatar_url && (
                  <img src={discordUser.avatar_url} alt="" className="h-8 w-8 rounded-full" />
                )}
                <span className="font-bold text-white/60">
                  Submitting as <span className="font-black text-white">{discordUser?.full_name || discordUser?.name}</span>
                </span>
                <button
                  type="button"
                  onClick={() => supabase.auth.signOut()}
                  className="ml-auto text-sm font-black uppercase text-white/30 hover:text-white/60"
                >
                  Sign out
                </button>
              </div>

              <div className="grid gap-6">
                <label className="grid gap-2">
                  <span className="font-black uppercase text-[#ffb56f]">Roblox Username <span className="text-[#ffd978]">*</span></span>
                  <input
                    className={inputClass}
                    type="text"
                    required
                    placeholder="Your exact Roblox username"
                    value={form.roblox_username}
                    onChange={e => set("roblox_username", e.target.value)}
                  />
                </label>

                <label className="grid gap-2">
                  <span className="font-black uppercase text-[#ffb56f]">Why were you banned? <span className="text-[#ffd978]">*</span></span>
                  <textarea
                    className={`${inputClass} min-h-[100px]`}
                    required
                    placeholder="Describe what happened / what rule you broke"
                    value={form.ban_reason}
                    onChange={e => set("ban_reason", e.target.value)}
                  />
                </label>

                <label className="grid gap-2">
                  <span className="font-black uppercase text-[#ffb56f]">Why should you be unbanned? <span className="text-[#ffd978]">*</span></span>
                  <textarea
                    className={`${inputClass} min-h-[140px]`}
                    required
                    placeholder="Make your case. Be honest and specific."
                    value={form.appeal_text}
                    onChange={e => set("appeal_text", e.target.value)}
                  />
                </label>

                <label className="grid gap-2">
                  <span className="font-black uppercase text-[#ffb56f]">Evidence <span className="text-white/30 normal-case font-bold">(optional)</span></span>
                  <input
                    className={inputClass}
                    type="url"
                    placeholder="Link to any evidence (video, screenshot, etc.)"
                    value={form.evidence}
                    onChange={e => set("evidence", e.target.value)}
                  />
                </label>
              </div>

              {error && (
                <div className="mt-6 rounded-xl bg-red-500/20 px-4 py-3 font-bold text-red-400">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="outfall-button mt-8 disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit Appeal"}
              </button>
            </form>
          )}
        </div>
      </section>
    </main>
  );
}
